import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/contexts/CartContext";
import { useLang } from "@/contexts/LanguageContext";
import { BRAND_CONTACT } from "@/data/products";
import { Minus, Plus, X } from "lucide-react";

const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal, clear } = useCart();
  const { t, lang } = useLang();

  const formatLines = () => {
    return items
      .map(
        (i) =>
          `• ${i.name[lang] || i.name.es} × ${i.qty}`
      )
      .join("\n");
  };

  const checkoutWhatsApp = () => {
    const msg = encodeURIComponent(
      `Hola La Criollita,%0A%0AMe gustaría solicitar presupuesto para el siguiente pedido:%0A%0A${formatLines()}%0A%0AGracias.`
    );
    window.open(`https://wa.me/${BRAND_CONTACT.whatsapp}?text=${msg}`, "_blank");
  };

  const checkoutEmail = () => {
    const body = encodeURIComponent(
      `Hola La Criollita,\n\nMe gustaría solicitar presupuesto para el siguiente pedido:\n\n${formatLines()}\n\nGracias.`
    );
    window.location.href = `mailto:${BRAND_CONTACT.email}?subject=Solicitud de presupuesto — La Criollita&body=${body}`;
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="bg-bg border-l border-bone w-full sm:max-w-md p-0 flex flex-col"
      >
        <SheetHeader className="px-8 py-7 border-b border-bone">
          <SheetTitle className="font-serif text-3xl text-ink tracking-tight m-0">
            {t.shop.cartTitle}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-8 py-6">
          {items.length === 0 ? (
            <div
              data-testid="cart-empty"
              className="h-full flex items-center justify-center text-ink-muted font-light text-center"
            >
              {t.shop.empty}
            </div>
          ) : (
            <ul className="divide-y divide-bone">
              {items.map((it) => (
                <li
                  key={it.id}
                  data-testid={`cart-item-${it.id}`}
                  className="py-6 flex gap-4"
                >
                  <img
                    src={it.image}
                    alt=""
                    className="w-20 h-20 object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h5 className="font-serif text-lg text-ink leading-tight mb-3">
                      {it.name[lang] || it.name.es}
                    </h5>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center border border-bone">
                        <button
                          data-testid={`cart-qty-minus-${it.id}`}
                          onClick={() => updateQty(it.id, it.qty - 1)}
                          className="w-8 h-8 flex items-center justify-center text-ink hover:bg-surface transition-colors"
                          aria-label="-"
                        >
                          <Minus className="w-3 h-3" strokeWidth={1.5} />
                        </button>
                        <span
                          data-testid={`cart-qty-${it.id}`}
                          className="w-10 text-center text-sm font-medium"
                        >
                          {it.qty}
                        </span>
                        <button
                          data-testid={`cart-qty-plus-${it.id}`}
                          onClick={() => updateQty(it.id, it.qty + 1)}
                          className="w-8 h-8 flex items-center justify-center text-ink hover:bg-surface transition-colors"
                          aria-label="+"
                        >
                          <Plus className="w-3 h-3" strokeWidth={1.5} />
                        </button>
                      </div>
                      <button
                        data-testid={`cart-remove-${it.id}`}
                        onClick={() => removeItem(it.id)}
                        className="text-ink-muted hover:text-wine ml-auto"
                        aria-label={t.shop.remove}
                      >
                        <X className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-bone px-8 py-6 bg-surface/40">
            <p className="text-xs text-ink-muted font-light leading-relaxed mb-5">
              Solicita presupuesto para tu pedido por WhatsApp o e-mail. Te respondemos en menos de 24h con precio, stock y fecha de entrega.
            </p>
            <div className="space-y-3">
              <button
                data-testid="checkout-whatsapp-btn"
                onClick={checkoutWhatsApp}
                className="w-full bg-wine text-bg py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-wine-hover transition-colors"
              >
                {t.shop.checkoutWA}
              </button>
              <button
                data-testid="checkout-email-btn"
                onClick={checkoutEmail}
                className="w-full bg-transparent text-ink border border-ink py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-ink hover:text-bg transition-colors"
              >
                {t.shop.checkoutEmail}
              </button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
